import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import getApiBase from '../utils/apiBase';
import { useI18n } from '../i18n/I18nContext';
import './AdminProducts.css';
import './AdminCommunity.css';

const API_BASE = getApiBase();
const REPORT_REASONS = ['spam', 'harassment', 'inappropriate', 'other'];
const CONTENT_TYPES = ['score', 'reply', 'article', 'comment'];
const REPORT_STATUSES = ['pending', 'resolved', 'dismissed'];

const requestErrorKey = (status, fallback) => {
  if (status === 401 || status === 403) return 'admin.community.accessDenied';
  if (status === 404) return 'admin.community.notFound';
  if (status === 429) return 'admin.community.rateLimited';
  if (status === 400) return 'admin.community.invalidAction';
  return fallback;
};

const AdminCommunity = () => {
  const { t } = useI18n();
  const [auth, setAuth] = useState(localStorage.getItem('adminAuth') || '');
  const [login, setLogin] = useState({ username: '', password: '' });
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = auth ? { Authorization: `Basic ${auth}` } : {};
  const loadReports = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/community/reports?status=all`, {
        credentials: 'include',
        headers: auth ? { Authorization: `Basic ${auth}` } : {},
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setStatus(requestErrorKey(response.status, 'admin.community.loadFailed'));
        return;
      }
      setReports(Array.isArray(data.items) ? data.items : []);
    } catch {
      setStatus('admin.community.loadFailed');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const loginAdmin = (event) => {
    event.preventDefault();
    const token = btoa(`${login.username}:${login.password}`);
    localStorage.setItem('adminAuth', token);
    setAuth(token);
    setStatus('admin.loggedIn');
  };

  const logoutAdmin = () => {
    localStorage.removeItem('adminAuth');
    setAuth('');
    setReports([]);
    setLogin({ username: '', password: '' });
    setStatus('admin.loggedOut');
  };

  const write = async (path, body) => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'PATCH', credentials: 'include', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!response.ok) throw Object.assign(new Error(), { status: response.status });
  };

  const moderate = async (report, action) => {
    try {
      await write(`/api/admin/community/content/${report.target_type}/${report.target_id}`, { action });
      setStatus(action === 'hide' ? 'admin.community.hidden' : 'admin.community.restored');
      await loadReports();
    } catch (error) {
      setStatus(requestErrorKey(error.status, 'admin.community.moderationFailed'));
    }
  };

  const resolve = async (report, nextStatus) => {
    try {
      await write(`/api/admin/community/reports/${report.id}`, { status: nextStatus });
      setStatus(nextStatus === 'resolved' ? 'admin.community.reportResolved' : 'admin.community.reportDismissed');
      await loadReports();
    } catch (error) {
      setStatus(requestErrorKey(error.status, 'admin.community.moderationFailed'));
    }
  };

  return (
    <main className="admin-products">
      <Seo title={t('admin.community.title')} description={t('admin.community.seoDescription')} path="/admin/community" noindex />
      <header className="admin-header">
        <div>
          <h1>{t('admin.community.title')}</h1>
          {auth && <Link className="admin-nav-link" to="/admin">{t('admin.nav.products')}</Link>}
          <Link className="admin-nav-link" to="/admin/users">{t('admin.nav.users')}</Link>
        </div>
        {auth && <button type="button" onClick={logoutAdmin}>{t('admin.logout')}</button>}
      </header>
      {status && <p role="status">{t(status)}</p>}
      {!auth ? (
        <form onSubmit={loginAdmin} className="login-card">
          <h2>{t('admin.loginTitle')}</h2>
          <input aria-label={t('admin.username')} placeholder={t('admin.username')} value={login.username} onChange={(event) => setLogin({ ...login, username: event.target.value })} required />
          <input type="password" aria-label={t('admin.password')} placeholder={t('admin.password')} value={login.password} onChange={(event) => setLogin({ ...login, password: event.target.value })} required />
          <button type="submit">{t('admin.login')}</button>
        </form>
      ) : (
        <>
          <button type="button" onClick={loadReports} disabled={loading}>{t(loading ? 'admin.loading' : 'admin.community.refreshReports')}</button>
          <div className="community-admin-list">
            {reports.map((report) => (
              <article key={report.id} className="community-admin-report">
                <h3>{t(`community.report.${REPORT_REASONS.includes(report.reason) ? report.reason : 'other'}`)} · {t(`admin.community.type.${CONTENT_TYPES.includes(report.target_type) ? report.target_type : 'unknown'}`)}</h3>
                <p>{t('admin.community.reportedBy', { name: report.reporter_name, status: t(`admin.community.status.${REPORT_STATUSES.includes(report.status) ? report.status : 'unknown'}`) })}</p>
                {report.details && <p>{report.details}</p>}
                <pre>{report.target ? (report.target.comment || report.target.content || t('admin.community.noText')) : t('admin.community.contentMissing')}</pre>
                <div className="community-admin-actions">
                  <button type="button" onClick={() => moderate(report, 'hide')}>{t('admin.community.hide')}</button>
                  <button type="button" onClick={() => moderate(report, 'restore')}>{t('admin.community.restore')}</button>
                  {report.status === 'pending' && <>
                    <button type="button" onClick={() => resolve(report, 'resolved')}>{t('admin.community.resolve')}</button>
                    <button type="button" onClick={() => resolve(report, 'dismissed')}>{t('admin.community.dismiss')}</button>
                  </>}
                </div>
              </article>
            ))}
            {!loading && !reports.length && <p>{t('admin.community.empty')}</p>}
          </div>
        </>
      )}
    </main>
  );
};

export default AdminCommunity;
